import React, { useEffect, useMemo, useCallback, memo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { usePageTitle } from 'utils/pageTitle';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { openModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';
import Spinner from 'components/Spinner';
import './OrganizationsPage.css';

// Memoized Subscription Badge Component
const SubscriptionBadge = memo(({ subscription }) => {
	const badgeClass = useMemo(() => {
		if (!subscription) return 'bg-success';

		const planName = subscription.plan_name?.toLowerCase() || '';

		if (planName.includes('enterprise')) return 'bg-dark';
		if (planName.includes('business')) return 'bg-primary';
		if (planName.includes('pro')) return 'bg-info';
		if (planName.includes('starter')) return 'bg-success';
		return 'bg-success';
	}, [subscription]);

	const planName = useMemo(() => {
		return subscription?.plan_name || subscription?.plan?.name || 'Free';
	}, [subscription]);

	return (
		<span
			className={`badge ${badgeClass} organizations-page__subscription-badge`}
		>
			<i className="bi bi-star-fill me-1"></i>
			{planName}
		</span>
	);
});

SubscriptionBadge.displayName = 'SubscriptionBadge';

// Memoized Usage Progress Bar Component
const UsageProgressBar = memo(
	({ label, used, total, icon, showWarning = true }) => {
		const percentage = useMemo(() => {
			if (total === -1) return 0; // Unlimited
			if (total === 0) return 0;
			return Math.min((used / total) * 100, 100);
		}, [used, total]);

		const barClass = useMemo(() => {
			if (total === -1) return 'bg-primary'; // Unlimited
			if (percentage >= 90) return 'bg-danger';
			if (percentage >= 75) return 'bg-warning';
			return 'bg-success';
		}, [percentage, total]);

		const displayText = useMemo(() => {
			if (total === -1) return `${used} / Unlimited`;
			return `${used} / ${total}`;
		}, [used, total]);

		const showWarningBadge = useMemo(() => {
			return showWarning && total !== -1 && percentage >= 90;
		}, [showWarning, total, percentage]);

		return (
			<div className="organizations-page__usage-item">
				<div className="organizations-page__usage-header">
					<div className="organizations-page__usage-label">
						<i className={`bi ${icon} me-2 text-muted`}></i>
						<span className="fw-bold">{label}</span>
					</div>
					<div className="organizations-page__usage-value">
						<span className="text-muted small">{displayText}</span>
						{showWarningBadge && (
							<span className="badge bg-danger ms-2">
								<i className="bi bi-exclamation-triangle"></i>
							</span>
						)}
					</div>
				</div>
				<div className="progress organizations-page__usage-progress">
					<div
						className={`progress-bar ${barClass}`}
						role="progressbar"
						style={{ width: `${total === -1 ? 100 : percentage}%` }}
						aria-valuenow={percentage}
						aria-valuemin="0"
						aria-valuemax="100"
					></div>
				</div>
			</div>
		);
	},
);

UsageProgressBar.displayName = 'UsageProgressBar';

// Memoized Organization Card Component with Subscription
const OrganizationCard = memo(
	({ organization, isActive, onEdit, onDelete, onViewMembers }) => {
		const [stats, setStats] = useState(null);
		const [isLoadingStats, setIsLoadingStats] = useState(false);
		const { actions } = useOrganizations();

		// Load stats on mount
		useEffect(() => {
			const loadStats = async () => {
				setIsLoadingStats(true);
				try {
					const result = await actions.getStats({ id: organization.id });
					setStats(result);
				} catch (error) {
					console.error('Failed to load organization stats:', error);
				} finally {
					setIsLoadingStats(false);
				}
			};

			loadStats();
		}, [organization.id, actions]);

		// Memoized subscription data
		const subscription = useMemo(() => {
			return stats?.subscription || organization.subscription || null;
		}, [stats, organization]);

		// Memoized stats data
		const statsData = useMemo(() => {
			return {
				portfolioSets:
					stats?.portfolio_sets_count || organization.portfolio_sets_count || 0,
				properties:
					stats?.properties_count || organization.properties_count || 0,
				members: stats?.members_count || organization.members_count || 0,
			};
		}, [stats, organization]);

		// Memoized limits from stats/subscription
		const limits = useMemo(() => {
			if (stats) {
				return {
					properties: stats.properties_limit || 30,
					seats: stats.seats_limit || 3,
				};
			}
			if (subscription?.limits) {
				return {
					properties: subscription.limits.properties || 30,
					seats: subscription.limits.seats || 3,
				};
			}
			return {
				properties: 30, // Default free plan
				seats: 3,
			};
		}, [stats, subscription]);

		// Memoized role badge color
		const roleBadgeClass = useMemo(() => {
			switch (organization.user_role) {
				case 'owner':
					return 'bg-primary';
				case 'admin':
					return 'bg-info';
				case 'member':
					return 'bg-secondary';
				default:
					return 'bg-light text-dark';
			}
		}, [organization.user_role]);

		// Memoized initials
		const initials = useMemo(() => {
			if (!organization.name) return '??';
			const parts = organization.name.split(' ').filter((p) => p.length > 0);
			if (parts.length >= 2) {
				return (parts[0][0] + parts[1][0]).toUpperCase();
			}
			return organization.name.substring(0, 2).toUpperCase();
		}, [organization.name]);

		// Memoized subscription status
		const subscriptionStatus = useMemo(() => {
			if (!subscription) return 'inactive';
			return subscription.status || 'active';
		}, [subscription]);

		return (
			<div
				className={`organizations-page__card ${
					isActive ? 'organizations-page__card--active' : ''
				}`}
			>
				{isActive && (
					<div className="organizations-page__card-badge">
						<span className="badge bg-success">
							<i className="bi bi-check-circle me-1"></i>
							Selected
						</span>
					</div>
				)}

				{/* Card Header */}
				<div className="organizations-page__card-header">
					<div className="organizations-page__card-icon">{initials}</div>
					<div className="organizations-page__card-title-section">
						<h4 className="organizations-page__card-title">
							{organization.name}
						</h4>
						<div className="organizations-page__card-badges">
							<span className={`badge ${roleBadgeClass} me-2`}>
								{organization.user_role}
							</span>
							{organization.membership_type === 'owned' ? (
								<span className="badge bg-success">Personal</span>
							) : (
								<span className="badge bg-warning">Shared</span>
							)}
						</div>
					</div>
				</div>

				{/* Description */}
				{organization.description && (
					<p className="organizations-page__card-description">
						{organization.description}
					</p>
				)}

				{/* Subscription Info */}
				<div className="organizations-page__subscription-section">
					<div className="organizations-page__subscription-header">
						<SubscriptionBadge subscription={subscription} />
						{subscription && subscriptionStatus !== 'active' && (
							<span className="badge bg-warning ms-2">
								{subscriptionStatus}
							</span>
						)}
					</div>

					{/* Usage Bars */}
					{isLoadingStats ? (
						<div className="text-center py-3">
							<div
								className="spinner-border spinner-border-sm text-primary"
								role="status"
							>
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<div className="organizations-page__usage-section">
							<UsageProgressBar
								label="Properties"
								used={statsData.properties}
								total={limits.properties}
								icon="bi-building"
							/>
							<UsageProgressBar
								label="Members"
								used={statsData.members}
								total={limits.seats}
								icon="bi-people"
							/>
						</div>
					)}
				</div>

				{/* Stats Grid */}
				<div className="organizations-page__card-stats">
					<div className="organizations-page__stat">
						<div className="organizations-page__stat-value">
							{statsData.portfolioSets}
						</div>
						<div className="organizations-page__stat-label">Portfolios</div>
					</div>
					<div className="organizations-page__stat">
						<div className="organizations-page__stat-value">
							{statsData.properties}
						</div>
						<div className="organizations-page__stat-label">Properties</div>
					</div>
					<div className="organizations-page__stat">
						<div className="organizations-page__stat-value">
							{statsData.members}
						</div>
						<div className="organizations-page__stat-label">Members</div>
					</div>
				</div>

				{/* Actions */}
				<div className="organizations-page__card-actions">
					<button
						className="btn btn-outline-secondary btn-sm"
						onClick={() => onViewMembers(organization.id)}
					>
						<i className="bi bi-people me-1"></i>
						Members
					</button>
					{/* {false && organization.user_role === 'owner' && (
						<>
							<button
								className="btn btn-outline-secondary btn-sm"
								onClick={() => onEdit(organization.id)}
							>
								<i className="bi bi-pencil"></i>
							</button>
							<button
								className="btn btn-outline-danger btn-sm"
								onClick={() => onDelete(organization.id)}
							>
								<i className="bi bi-trash"></i>
							</button>
						</>
					)} */}
				</div>
			</div>
		);
	},
);

OrganizationCard.displayName = 'OrganizationCard';

// Main Component (unchanged from previous version)
const OrganizationsPage = memo(() => {
	usePageTitle('Organizations');
	const history = useHistory();
	const dispatch = useDispatch();

	const {
		items: organizations,
		isLoading,
		active,
		actions,
		error,
	} = useOrganizations();

	// Load organizations on mount
	useEffect(() => {
		actions.fetchAll();
		actions.getActive();
	}, [actions]);

	// Memoized active organization ID
	const activeOrgId = useMemo(() => active?.id, [active]);

	// Handlers with useCallback
	const handleEdit = useCallback(
		(id) => {
			dispatch(
				openModal(MODAL_TYPES.EDIT_ORGANIZATION, {
					organizationId: id,
					onSuccess: () => {
						actions.fetchAll();
					},
				}),
			);
		},
		[dispatch, actions],
	);

	const handleDelete = useCallback(
		async (id) => {
			dispatch(
				openModal(MODAL_TYPES.CONFIRM, {
					title: 'Delete Organization',
					message:
						'Are you sure you want to delete this organization? This action cannot be undone.',
					confirmLabel: 'Delete',
					color: 'danger',
					onConfirm: async () => {
						try {
							await actions.delete(id);
							dispatch(
								showToast({
									title: 'Success',
									body: 'Organization deleted successfully',
									type: 'success',
								}),
							);
							actions.fetchAll();
						} catch (error) {
							dispatch(
								showToast({
									title: 'Error',
									body: error.message || 'Failed to delete organization',
									type: 'error',
								}),
							);
						}
					},
				}),
			);
		},
		[actions, dispatch],
	);

	const handleViewMembers = useCallback(
		(id) => {
			history.push(`/dashboard/organization/${id}/members`);
		},
		[history],
	);

	if (isLoading && organizations.length === 0) {
		return (
			<DashboardLayout>
				<div className="text-center py-5">
					<Spinner />
				</div>
			</DashboardLayout>
		);
	}

	if (error && organizations.length === 0) {
		return (
			<DashboardLayout>
				<div className="alert alert-danger">
					<i className="bi bi-exclamation-triangle me-2"></i>
					Error loading organizations: {error}
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="organizations-page v3-scope">
				<div className="dashboard-section">
					<div className="dashboard-section-header">
						<h1>
							<i className="bi bi-building me-3" />
							Organizations
						</h1>
					</div>

					<div className="dashboard-section-body">
						{organizations.length === 0 ? (
							<div className="organizations-page__empty">
								<i className="bi bi-building organizations-page__empty-icon"></i>
								<h3>No Organizations Found</h3>
								<p className="text-muted">
									Contact support if you believe this is an error
								</p>
							</div>
						) : (
							<div className="organizations-page__grid">
								{organizations.map((org) => (
									<OrganizationCard
										key={org.id}
										organization={org}
										isActive={org.id === activeOrgId}
										onEdit={handleEdit}
										onDelete={handleDelete}
										onViewMembers={handleViewMembers}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
});

OrganizationsPage.displayName = 'OrganizationsPage';

export default OrganizationsPage;
